import React, { useState, useEffect } from 'react';
import { CheckCircle, Package } from 'lucide-react';

interface Institute {
  _id: string;
  id?: string;
  name: string;
}

interface InstituteSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  institutes: Institute[];
  selectedInstitutes: string[];
  onConfirm: (selected: string[]) => void;
}

const InstituteSelectionModal: React.FC<InstituteSelectionModalProps> = ({
  isOpen,
  onClose,
  institutes,
  selectedInstitutes,
  onConfirm
}) => {
  const [selected, setSelected] = useState<string[]>(selectedInstitutes);

  useEffect(() => {
    setSelected(selectedInstitutes);
  }, [selectedInstitutes]);

  if (!isOpen) return null;

  const handleToggle = (instituteId: string) => {
    if (selected.includes(instituteId)) {
      setSelected(selected.filter(id => id !== instituteId));
    } else {
      setSelected([...selected, instituteId]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === institutes.length) {
      setSelected([]);
    } else {
      setSelected(institutes.map(inst => inst._id || inst.id || ''));
    }
  };

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  const progress = institutes.length > 0 ? Math.round((selected.length / institutes.length) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 rounded-t-2xl">
          <div className="flex items-center gap-3 text-white">
            <Package className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Institutes</h2>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8">
          {/* Select All */}
          <div className="mb-6 pb-4 border-b-2 border-gray-200">
            <label className="flex items-center gap-3 p-4 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={selected.length === institutes.length && institutes.length > 0}
                onChange={handleSelectAll}
                className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-lg font-bold text-blue-600">Select All</span>
            </label>
          </div>

          {/* Institute Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {institutes.map((institute) => {
              const instituteId = institute._id || institute.id || '';
              const isSelected = selected.includes(instituteId);

              return (
                <label
                  key={instituteId}
                  className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                    isSelected
                      ? 'bg-green-50 border-green-400 shadow-md'
                      : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggle(instituteId)}
                    className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className={`text-base font-semibold flex-1 ${isSelected ? 'text-green-700' : 'text-gray-700'}`}>
                    {institute.name}
                  </span>
                  {isSelected && <CheckCircle className="w-6 h-6 text-green-600" />}
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">
                Progress: {selected.length} of {institutes.length} completed
              </span>
              <span className="text-sm font-bold text-blue-600">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-300 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.length === 0}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstituteSelectionModal;
